import { useCallback, useContext, useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { useEnableNewHotkeySystem } from '../../feature-flags/hooks/use-enable-new-hotkey-system';
import { Context as GlobalEventListenerContext } from '../components/global-event-listener/context';

type UseClickOutsideOptions = {
  enabled?: boolean;
};

type UseClickOutsideResult = {
  // Call this from a React capture-phase handler (onMouseDownCapture / onTouchStartCapture)
  // on the component's wrapper to signal that the interaction originated inside the React
  // subtree. The document listener reads and resets this flag before deciding to fire.
  markInteractionInside: () => void;
};

export const useClickOutside = (
  ref: RefObject<HTMLElement | null>,
  onClickOutside: (event: MouseEvent) => void,
  { enabled = true }: UseClickOutsideOptions = {},
): UseClickOutsideResult => {
  const isNewHotkeySystemEnabled = useEnableNewHotkeySystem();
  const { subscribe } = useContext(GlobalEventListenerContext);

  // Tracks whether the current event originated inside the React subtree.
  // React capture-phase handlers fire at the root container (before document bubble-phase),
  // so this flag is always set before the document listener reads it.
  const interactionInsideRef = useRef(false);
  const markInteractionInside = useCallback(() => {
    interactionInsideRef.current = true;
  }, []);

  // GEL path: subscribe to onClick queue when flag is OFF
  useEffect(() => {
    if (isNewHotkeySystemEnabled) {
      return;
    }
    if (!enabled) {
      return;
    }

    const handler = (event: Event) => {
      if (!ref.current) {
        return;
      }
      if (ref.current.contains(event.target as Node)) {
        return;
      }
      if ((event.target as Element).closest('[data-ignore-click-outside]')) {
        return;
      }
      onClickOutside(event as MouseEvent);
    };

    return subscribe('onClick', handler);
  }, [isNewHotkeySystemEnabled, enabled, ref, onClickOutside, subscribe]);

  // Native path: document mousedown/touchstart listeners when flag is ON.
  // interactionInsideRef is read+reset at handler entry — not a dep — so it always
  // reflects the most recent React capture-phase signal without re-attaching.
  useEffect(() => {
    if (!isNewHotkeySystemEnabled) {
      return;
    }

    const handler = (event: MouseEvent | TouchEvent) => {
      // Always reset so the flag never goes stale across events.
      const wasInside = interactionInsideRef.current;
      interactionInsideRef.current = false;

      if (!enabled) {
        return;
      }
      if (!ref.current) {
        return;
      }

      // Interaction originated inside the React subtree (portal-transparent).
      if (wasInside) {
        return;
      }

      const target = (event as MouseEvent).target ?? (event as TouchEvent).touches[0]?.target;
      if (!(target instanceof Node)) {
        return;
      }

      // DOM fallback for inline (non-portalled) content.
      if (ref.current.contains(target)) {
        return;
      }

      if ((target as Element).closest?.('[data-ignore-click-outside]')) {
        return;
      }

      onClickOutside(event as MouseEvent);
    };

    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler as EventListener);

    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler as EventListener);
    };
  }, [isNewHotkeySystemEnabled, enabled, ref, onClickOutside]);

  return { markInteractionInside };
};
